import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

import { MailIcon } from "@lucide/icons";

import type { IdeaWithCoverImage, OpenAPI } from "simplydo/interfaces";
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback";
import { Button } from "simplydo/react/ui/button";
import { H4 } from "simplydo/react/ui/header";
import { Message } from "simplydo/react/ui/message";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Table, TableBody, TableCell, TableRow } from "simplydo/react/ui/table";

import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import BaseChip from "components/lib/Chips/BaseChip";
import type { ChipUser } from "components/lib/Chips/types";
import {
  AssessmentRoundLabel,
  RoundLabelsGroup,
} from "components/lib/UI/AssessmentRoundLabel";

import type { AssignableUser, LocalAssignments } from "./AssignmentDashboard";
import { ReassignAssessmentsPanel } from "./ReassignAssessmentsPanel";

type AssessmentRound = NonNullable<
  NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
>[number];

type SelectedAssignment = { ideaId: string; assessmentRoundId: string };

type UserInspectModalProps = {
  challengeId: string;
  inspectedAssessor: AssignableUser | null;
  setInspectedAssessor: Dispatch<SetStateAction<AssignableUser | null>>;
  localAssignments: LocalAssignments[string]["ideas"] | null | undefined;
  setLocalAssignments: Dispatch<SetStateAction<LocalAssignments>>;
  assessableIdeasById: Record<string, OpenAPI.Schemas["Idea"]>;
  unassignAssessor: (
    ideaId: string,
    assessorId: string,
    assessmentRoundId: string,
  ) => void;
  reassignActiveAssessments: (
    fromAssessorId: string,
    assignments: { ideaId: string; assessmentRoundId: string }[],
    target: AssignableUser,
  ) => void;
  dashboardUsers: AssignableUser[];
  dashboardEmails: AssignableUser[];
  assessmentRoundMap: Record<string, AssessmentRound>;
};

const assignmentKey = (assignment: SelectedAssignment) =>
  `${assignment.ideaId}:${assignment.assessmentRoundId}`;

const hasAssignments = (
  assignments: AssignableUser["assignments"] | null | undefined,
) => {
  if (!assignments) return false;

  return Object.values(assignments).some(
    (assignment) => assignment.assessmentRoundIds?.length > 0,
  );
};

export const UserInspectModal = ({
  challengeId,
  inspectedAssessor,
  setInspectedAssessor,
  localAssignments,
  setLocalAssignments,
  assessableIdeasById,
  unassignAssessor,
  reassignActiveAssessments,
  dashboardUsers,
  dashboardEmails,
  assessmentRoundMap,
}: UserInspectModalProps) => {
  const { t } = useTranslation();

  const [reassignMode, setReassignMode] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState<
    SelectedAssignment[]
  >([]);
  const [targetAssessor, setTargetAssessor] = useState<AssignableUser | null>(
    null,
  );

  const resetReassignState = useCallback(() => {
    setReassignMode(false);
    setSelectedAssignments([]);
    setTargetAssessor(null);
  }, []);

  useEffect(() => {
    if (!inspectedAssessor) {
      resetReassignState();
    }
  }, [inspectedAssessor, resetReassignState]);

  const selectedKeys = useMemo(
    () => new Set(selectedAssignments.map(assignmentKey)),
    [selectedAssignments],
  );

  const isAssignmentSelected = useCallback(
    (ideaId: string, assessmentRoundId: string) =>
      selectedKeys.has(assignmentKey({ ideaId, assessmentRoundId })),
    [selectedKeys],
  );

  const toggleAssignment = useCallback(
    (ideaId: string, assessmentRoundId: string) => {
      setSelectedAssignments((prev) => {
        const key = assignmentKey({ ideaId, assessmentRoundId });
        const exists = prev.some((item) => assignmentKey(item) === key);
        if (exists) {
          return prev.filter((item) => assignmentKey(item) !== key);
        }
        return [...prev, { ideaId, assessmentRoundId }];
      });
    },
    [],
  );

  const handleConfirmReassignment = useCallback(() => {
    if (
      !inspectedAssessor ||
      !targetAssessor ||
      selectedAssignments.length === 0
    ) {
      return;
    }

    const assignableTargetPool = targetAssessor.isEmailInvitee
      ? dashboardEmails
      : dashboardUsers;
    const assignableTarget =
      assignableTargetPool.find(
        (assessor) => assessor._id === targetAssessor._id,
      ) ?? targetAssessor;

    reassignActiveAssessments(
      inspectedAssessor._id,
      selectedAssignments.map(({ ideaId, assessmentRoundId }) => ({
        ideaId,
        assessmentRoundId,
      })),
      assignableTarget,
    );

    resetReassignState();
  }, [
    inspectedAssessor,
    targetAssessor,
    selectedAssignments,
    dashboardUsers,
    dashboardEmails,
    reassignActiveAssessments,
    resetReassignState,
  ]);

  const hasDraftAssignments = hasAssignments(localAssignments);
  const hasActiveAssignments = hasAssignments(inspectedAssessor?.assignments);

  const canReassign =
    !inspectedAssessor?.isEmailInvitee && hasActiveAssignments;

  const renderRoundLabel = (
    ideaId: string,
    assessmentRoundId: string,
    isDraft: boolean,
    onDelete?: () => void,
  ) => {
    const round = assessmentRoundMap[assessmentRoundId];
    if (!round) return null;

    return (
      <AssessmentRoundLabel
        key={`${isDraft}:${round.id}`}
        assessmentRound={round}
        selectable={reassignMode && !isDraft}
        selected={isAssignmentSelected(ideaId, round.id)}
        onToggle={() => toggleAssignment(ideaId, round.id)}
        onDelete={reassignMode ? undefined : onDelete}
      />
    );
  };

  return (
    <Modal
      open={!!inspectedAssessor}
      onClose={() => {
        resetReassignState();
        setInspectedAssessor(null);
      }}
    >
      {inspectedAssessor ? (
        <>
          <ModalHeader>
            Assessments for{" "}
            {inspectedAssessor.isEmailInvitee
              ? inspectedAssessor.email
              : inspectedAssessor.ownerAssessor.profile.fullName}
          </ModalHeader>
          <ModalContent scrolling>
            {inspectedAssessor.isEmailInvitee ? (
              <BaseChip
                icon={MailIcon}
                title={inspectedAssessor.email}
                content={`This is a pending invitation, the user has not yet signed up to ${util.appName()}.`}
              />
            ) : (
              <UserChip user={inspectedAssessor.ownerAssessor as ChipUser} />
            )}

            {reassignMode ? null : (
              <>
                <H4>Draft assessments</H4>

                {hasDraftAssignments ? (
                  <>
                    <Message color="blue">
                      These assignments are currently only in draft. Before the
                      users is notified the assignments must first be finalised
                      and saved.
                    </Message>
                    <Table>
                      <TableBody>
                        {Object.entries(localAssignments || {})
                          .filter(
                            ([ideaId, assignment]) =>
                              assignment?.assessmentRoundIds?.length > 0,
                          )
                          .map(([ideaId, assignment]) => (
                            <TableRow key={ideaId}>
                              <TableCell collapsing>
                                <ImageWithFallback
                                  avatar
                                  style={{
                                    minWidth: 35,
                                    width: 35,
                                    height: 25,
                                    borderRadius: 4,
                                    objectFit: "cover",
                                  }}
                                  src={util.ideaCoverImage(
                                    assessableIdeasById[
                                      ideaId
                                    ] as IdeaWithCoverImage,
                                    "200x200",
                                  )}
                                  fallbackSrc={util.ideaCoverImage(undefined)}
                                />
                              </TableCell>
                              <TableCell>
                                {assessableIdeasById[ideaId]?.name ||
                                  `Deleted ${t("generic.idea")}`}
                              </TableCell>
                              <TableCell collapsing textAlign="right">
                                <RoundLabelsGroup>
                                  {assignment?.assessmentRoundIds?.map(
                                    (assessmentRoundId) =>
                                      renderRoundLabel(
                                        ideaId,
                                        assessmentRoundId,
                                        true,
                                        () => {
                                          setLocalAssignments((prevState) => {
                                            const newState = {
                                              ...prevState,
                                            } as LocalAssignments;
                                            const assessorEntry =
                                              newState[inspectedAssessor._id];
                                            if (!assessorEntry) return newState;
                                            const ideaEntry =
                                              assessorEntry.ideas[ideaId];
                                            if (!ideaEntry) return newState;

                                            const round =
                                              assessmentRoundMap[
                                                assessmentRoundId
                                              ];
                                            if (!round) return newState;

                                            newState[inspectedAssessor._id] = {
                                              ...assessorEntry,
                                              ideas: {
                                                ...assessorEntry.ideas,
                                                [ideaId]: {
                                                  ...ideaEntry,
                                                  assessmentRoundIds:
                                                    ideaEntry.assessmentRoundIds.filter(
                                                      (id) => id !== round.id,
                                                    ),
                                                },
                                              },
                                            };

                                            const newRounds =
                                              newState[inspectedAssessor._id]
                                                ?.ideas?.[ideaId]
                                                ?.assessmentRoundIds;
                                            if (
                                              !newRounds ||
                                              newRounds.length === 0
                                            ) {
                                              delete newState[
                                                inspectedAssessor._id
                                              ]?.ideas[ideaId];
                                            }
                                            if (
                                              Object.keys(
                                                newState[inspectedAssessor._id]
                                                  ?.ideas || {},
                                              ).length === 0
                                            ) {
                                              delete newState[
                                                inspectedAssessor._id
                                              ];
                                            }

                                            return newState;
                                          });
                                        },
                                      ),
                                  )}
                                </RoundLabelsGroup>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </>
                ) : (
                  <Message>No draft assignments.</Message>
                )}
              </>
            )}

            <H4>Active assessments</H4>
            {inspectedAssessor.isEmailInvitee ? (
              <Message color="blue">
                This user has been invited but is currently not registered. To
                manage their invitation, please refer to the
                &quot;Dashboard&quot; tab.
              </Message>
            ) : null}
            {hasActiveAssignments ? (
              <Table>
                <TableBody>
                  {Object.entries(inspectedAssessor.assignments || {})
                    .filter(
                      ([ideaId, assignment]) =>
                        assignment?.assessmentRoundIds?.length > 0,
                    )
                    .map(([ideaId, assignment]) => (
                      <TableRow key={ideaId}>
                        <TableCell collapsing>
                          <ImageWithFallback
                            avatar
                            style={{
                              minWidth: 35,
                              width: 35,
                              height: 25,
                              borderRadius: 4,
                              objectFit: "cover",
                            }}
                            src={util.ideaCoverImage(
                              assessableIdeasById[ideaId] as IdeaWithCoverImage,
                              "200x200",
                            )}
                            fallbackSrc={util.ideaCoverImage(undefined)}
                          />
                        </TableCell>
                        <TableCell>
                          {assessableIdeasById[ideaId]?.name ||
                            `Deleted ${t("generic.idea")}`}
                        </TableCell>
                        <TableCell collapsing textAlign="right">
                          <RoundLabelsGroup>
                            {assignment?.assessmentRoundIds?.map(
                              (assessmentRoundId) =>
                                renderRoundLabel(
                                  ideaId,
                                  assessmentRoundId,
                                  false,
                                  inspectedAssessor.isEmailInvitee
                                    ? undefined
                                    : () => {
                                        unassignAssessor(
                                          ideaId,
                                          inspectedAssessor._id,
                                          assessmentRoundId,
                                        );
                                      },
                                ),
                            )}
                          </RoundLabelsGroup>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            ) : (
              <Message>No active assessments.</Message>
            )}

            {canReassign ? (
              <ReassignAssessmentsPanel
                canReassign={canReassign}
                reassignMode={reassignMode}
                selectedCount={selectedAssignments.length}
                challengeId={challengeId}
                excludeUserIds={[inspectedAssessor._id]}
                targetAssessor={targetAssessor}
                onTargetAssessorChange={setTargetAssessor}
                onStartReassign={() => setReassignMode(true)}
                onConfirm={handleConfirmReassignment}
                onCancel={resetReassignState}
              />
            ) : null}
          </ModalContent>
          <ModalActions>
            <Button
              onClick={() => {
                resetReassignState();
                setInspectedAssessor(null);
              }}
            >
              Close
            </Button>
          </ModalActions>
        </>
      ) : null}
    </Modal>
  );
};
